import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";
import type { FhirResource } from "../utils/fhir";

/**
 * Single-resource reads and instance `_history` go through the gateway's
 * generic admin passthrough (`GET /fhir/admin/{path}` → fhirserver), which
 * supports read, `_history`, and `vread`.
 *
 * NOTE: type-level search is intentionally NOT done via this passthrough — a
 * 2-segment path like `/fhir/admin/{Type}` collides with the gateway's
 * `/fhir/{resource_name}/{_id}` route and is misread as resource `admin`.
 * Patient browse therefore uses `Patient/{id}/$everything` (see below).
 *
 * NOTE (POC): this passthrough is currently only JWT-gated on the gateway;
 * a real role check is deferred to v2 (see plan). fhirserver still enforces
 * patient-compartment narrowing.
 */
const ADMIN_FHIR_BASE = "/fhir/admin";

// Admin reads are effectively point-in-time snapshots (version history never
// changes for a given versionId), so keep them fresh for a few minutes to
// avoid redundant refetches on window focus/reconnect.
const ADMIN_STALE_TIME = 5 * 60 * 1000;

/** A minimal FHIR Bundle shape (no Bundle class exists in js-fhir-tools). */
interface FhirBundle {
  resourceType: "Bundle";
  entry?: { resource?: FhirResource; fullUrl?: string }[];
  total?: number;
}

export interface ResourceVersion {
  versionId: string;
  lastUpdated?: string;
  resource: FhirResource;
}

/** Fetch the current value of a single resource by type + id. */
export const useResourceLookup = (resourceType?: string, id?: string) =>
  useApi<FhirResource>({
    queryOptions: {
      queryFn: async () => {
        const response = await axiosInstance.get<FhirResource>(
          `${ADMIN_FHIR_BASE}/${resourceType}/${id}`,
        );
        return response.data;
      },
      queryKey: QueryKeys.Admin.RESOURCE(resourceType, id),
      enabled: Boolean(resourceType && id),
      staleTime: ADMIN_STALE_TIME,
      retry: false,
    },
  });

/**
 * Fetch the full instance history, newest version first.
 * Parses the `_history` Bundle manually (each entry carries its own
 * `meta.versionId` / `meta.lastUpdated`).
 */
export const useResourceHistory = (resourceType?: string, id?: string) =>
  useApi<FhirBundle, ResourceVersion[]>({
    queryOptions: {
      queryFn: async () => {
        const response = await axiosInstance.get<FhirBundle>(
          `${ADMIN_FHIR_BASE}/${resourceType}/${id}/_history`,
          // Cap history depth so a long-lived resource can't return an
          // unbounded (and un-virtualized) timeline.
          { params: { _count: 100 } },
        );
        return response.data;
      },
      select: (bundle) =>
        (bundle.entry ?? [])
          .map((entry) => entry.resource)
          // Require a versionId — it's the stable React key and sort basis.
          .filter((r): r is FhirResource & { meta: { versionId: string } } =>
            Boolean(r?.meta?.versionId),
          )
          .map((resource) => ({
            versionId: resource.meta.versionId,
            lastUpdated: resource.meta.lastUpdated,
            resource,
          }))
          .sort((a, b) => Number(b.versionId || 0) - Number(a.versionId || 0)),
      queryKey: QueryKeys.Admin.HISTORY(resourceType, id),
      enabled: Boolean(resourceType && id),
      staleTime: ADMIN_STALE_TIME,
      retry: false,
    },
  });

/**
 * All of a patient's resources (patient-browse mode), via
 * `GET /fhir/Patient/{id}/$everything`. Returns a flat list of resources;
 * callers group/filter by `resourceType` client-side. This avoids the
 * gateway route collision that type-level `/fhir/admin/{Type}` search hits.
 */
export const usePatientEverything = (patientId?: string) =>
  useApi<FhirBundle, FhirResource[]>({
    queryOptions: {
      queryFn: async () => {
        const response = await axiosInstance.get<FhirBundle>(
          `/fhir/Patient/${patientId}/$everything`,
        );
        return response.data;
      },
      select: (bundle) =>
        (bundle.entry ?? [])
          .map((entry) => entry.resource)
          .filter((r): r is FhirResource => Boolean(r)),
      queryKey: QueryKeys.Admin.EVERYTHING(patientId),
      enabled: Boolean(patientId),
      staleTime: ADMIN_STALE_TIME,
      retry: false,
    },
  });
